import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Col, Row } from "@amzn/stencil-react-components/layout";
import {
  ModalBody,
  ModalFooter,
  ModalHeader,
} from "@amzn/stencil-react-components/modal";
import { Text } from "@amzn/stencil-react-components/text";
import { FormattedMessage } from "react-intl";

/** The Modal's `aria-labelledby` points here, which is the only way its role=dialog gets a name. */
export const MANUAL_HEADING_ID = "macOsPermissionsManualHeading";

interface ManualSetupModalProps {
  /** Starts the ordinary wizard, without the assistant. */
  readonly onBegin: () => void;
  /**
   * Whether the hire got here by reporting a grant they could not turn on.
   *
   * False when the app could not recognise a grant the Mac named, which lands here without anyone
   * having filed anything -- so the copy must not thank them for a report they never made.
   */
  readonly hasReported: boolean;
}

/**
 * Acknowledges that the assistant is out of reach, and sends the hire on to set up manually.
 *
 * A dialog over the walkthrough rather than a page replacing it, as the design has it: the wizard the
 * copy points at is the one behind this. Deliberately offers no dismissal -- the grants are required,
 * so there is nothing to retry, and going on is the only answer.
 */
export function ManualSetupModal({
  hasReported,
  onBegin,
}: ManualSetupModalProps): JSX.Element {
  return (
    <Col gridGap="S300" dataTestId="macOsPermissionsManualRoute">
      {/* No close control, so the acknowledgement cannot be dismissed past. */}
      <ModalHeader>
        <Text fontWeight="bold" id={MANUAL_HEADING_ID}>
          <FormattedMessage
            id={
              hasReported
                ? "macPermissions-manual-heading"
                : "macPermissions-manual-heading-unchecked"
            }
          />
        </Text>
      </ModalHeader>

      <ModalBody>
        <Text fontWeight="regular">
          <FormattedMessage
            id={
              hasReported
                ? "macPermissions-manual-description"
                : "macPermissions-manual-description-unchecked"
            }
          />
        </Text>
      </ModalBody>

      <ModalFooter>
        <Row gridGap="S400" justifyContent="flex-end">
          <Button
            variant={ButtonVariant.Primary}
            onClick={onBegin}
            dataTestId="macOsPermissionsBeginManualButton"
          >
            <FormattedMessage id="macPermissions-manual-begin-button" />
          </Button>
        </Row>
      </ModalFooter>
    </Col>
  );
}
